import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import "../styles/Privacy.css";

export default function PrivacyPage() {
  const lastUpdated = useMemo(() => {
    // Pick a stable date you will refresh when you edit the policy
    const d = new Date("2025-09-15T00:00:00");
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }, []);

  return (
    <div className="privacy-page">
      {/* HERO */}
      <section className="privacy-hero" aria-labelledby="privacy-hero-title">
        <div className="privacy-hero-bg" aria-hidden="true">
          <span className="p-orb orb-a"></span>
          <span className="p-orb orb-b"></span>
          <span className="p-orb orb-c"></span>
        </div>

        <div className="privacy-hero-inner">
          <h1 id="privacy-hero-title" className="privacy-hero-title">
            Privacy Policy
          </h1>
          <p className="privacy-hero-subtitle">
            We respect your privacy and protect the information you share with us. This page explains what we collect, how we use it, and the choices available to you.
          </p>

          <div className="privacy-hero-ctas">
            <Link to="/contact" className="p-btn primary">Contact Us</Link>
            <a href="#policy" className="p-btn secondary">Read The Full Policy</a>
            <button
              className="p-btn light"
              onClick={() => window.print()}
              aria-label="Print this policy"
            >
              Print
            </button>
          </div>

          <ul className="privacy-hero-chips" aria-label="Highlights">
            <li>No selling of personal information</li>
            <li>Contact details used for service only</li>
            <li>Simple opt out choices</li>
            <li>Clear deposit guidelines</li>
          </ul>

          <div className="privacy-stamp" aria-label="Last updated">
            Last updated {lastUpdated}
          </div>
        </div>
      </section>

      {/* TLDR */}
      <section className="privacy-tldr" aria-labelledby="tldr-title">
        <h2 id="tldr-title" className="privacy-section-title">In Short</h2>
        <div className="tldr-grid">
          <div className="tldr-card">
            <div className="tldr-head">What we collect</div>
            <p>Details you provide when you reach out, like name, email, phone, and your message. If you book visits or request a deposit, we store the related information needed to help you.</p>
          </div>
          <div className="tldr-card">
            <div className="tldr-head">How we use it</div>
            <p>To respond to questions, schedule visits, manage deposits, and share updates about availability or planned litters.</p>
          </div>
          <div className="tldr-card">
            <div className="tldr-head">What we do not do</div>
            <p>We do not sell personal information or share it for advertising. If we add analytics later, we will update this page first.</p>
          </div>
          <div className="tldr-card">
            <div className="tldr-head">Your choices</div>
            <p>Ask to view, correct, or delete your information at any time. You can opt out of non essential messages with a single request.</p>
          </div>
        </div>
      </section>

      {/* LOCAL TOC */}
      <nav className="privacy-toc" aria-label="On this page">
        <a href="#info">Information We Collect</a>
        <a href="#use">How We Use Information</a>
        <a href="#share">Sharing and Disclosure</a>
        <a href="#cookies">Cookies and Analytics</a>
        <a href="#retention">Data Retention</a>
        <a href="#minors">Children</a>
        <a href="#security">Security</a>
        <a href="#choices">Your Choices</a>
        <a href="#deposits">Deposits and Payments</a>
        <a href="#changes">Changes to this Policy</a>
        <a href="#contact">Contact</a>
      </nav>

      {/* POLICY BODY */}
      <section id="policy" className="privacy-body">

        <article id="info" className="privacy-section">
          <h2 className="privacy-section-title">Information We Collect</h2>
          <p>We collect information that you provide directly, such as name, email address, phone, and any details included in your message or form submission. If you request a visit or move forward with a deposit, we collect the information needed to schedule, confirm eligibility, and manage your reservation.</p>
          <details className="p-details">
            <summary>More detail</summary>
            <ul>
              <li>Contact details: name, email address, phone</li>
              <li>Inquiry details: preferred timing, interest in a specific puppy or upcoming litter, questions you ask</li>
              <li>Reservation details: deposit status, dates, and related confirmations</li>
              <li>Operational records: internal notes to help our team follow through with your request</li>
            </ul>
          </details>
        </article>

        <article id="use" className="privacy-section">
          <h2 className="privacy-section-title">How We Use Information</h2>
          <p>We use your information to respond to messages, plan visits, confirm availability, manage deposits, and provide updates you request. We may also use information to improve our site or services and to maintain accurate records.</p>
          <details className="p-details">
            <summary>Common examples</summary>
            <ul>
              <li>Replying to contact requests and answering questions</li>
              <li>Scheduling visits and sending confirmations</li>
              <li>Coordinating reservations and deposits when you choose to proceed</li>
              <li>Sending important service messages, such as changes to timing or availability</li>
            </ul>
          </details>
        </article>

        <article id="share" className="privacy-section">
          <h2 className="privacy-section-title">Sharing and Disclosure</h2>
          <p>We do not sell your personal information. We share information only as needed to provide our services, comply with the law, or protect our rights.</p>
          <details className="p-details">
            <summary>More detail</summary>
            <ul>
              <li>Service providers that support our site or email, such as hosting and storage</li>
              <li>Legal or safety reasons when we believe disclosure is required</li>
              <li>Business continuity, such as a transfer of our website or records if we merge or transition the operation of this site</li>
            </ul>
          </details>
        </article>

        <article id="cookies" className="privacy-section">
          <h2 className="privacy-section-title">Cookies and Analytics</h2>
          <p>We aim to keep tracking minimal. If we enable analytics or similar tools in the future, we will update this section to explain what runs on the site and how you can control it.</p>
          <details className="p-details">
            <summary>Current status</summary>
            <p>At this time we do not run third party advertising. If we add analytics, we will state which tools are in use and provide choices to opt out where available.</p>
          </details>
        </article>

        <article id="retention" className="privacy-section">
          <h2 className="privacy-section-title">Data Retention</h2>
          <p>We keep information for as long as needed to provide the service you requested and to meet legal or recordkeeping obligations. When information is no longer needed, we remove it or de identify it.</p>
        </article>

        <article id="minors" className="privacy-section">
          <h2 className="privacy-section-title">Children</h2>
          <p>Our site is intended for adults who can legally enter agreements. If we learn that we collected personal information from a child without a parent or guardian, we will delete it.</p>
        </article>

        <article id="security" className="privacy-section">
          <h2 className="privacy-section-title">Security</h2>
          <p>We use reasonable safeguards to protect the information we handle. No system can be perfectly secure, but we take care to limit access and use reputable services for storage and communication.</p>
        </article>

        <article id="choices" className="privacy-section">
          <h2 className="privacy-section-title">Your Choices and Rights</h2>
          <p>You can ask to view, update, or delete your information. You can also opt out of non essential messages at any time.</p>
          <details className="p-details">
            <summary>How to make a request</summary>
            <ul>
              <li>Send a note through our <Link to="/contact">contact page</Link> and tell us what you would like to do</li>
              <li>We will verify your request and respond as soon as we can</li>
            </ul>
          </details>
        </article>

        <article id="deposits" className="privacy-section">
          <h2 className="privacy-section-title">Deposits and Payments</h2>
          <p>Deposits are coordinated directly with our team. We will confirm timing, amount, and terms before you send funds. Payment details are handled by the payment method or provider you choose. We do not store complete card numbers on our site.</p>
          <details className="p-details">
            <summary>Important reminders</summary>
            <ul>
              <li>Puppies cannot be held without a deposit</li>
              <li>Always use the instructions we provide at the time of reservation</li>
              <li>Ask us any question before you send funds</li>
            </ul>
          </details>
        </article>

        <article id="changes" className="privacy-section">
          <h2 className="privacy-section-title">Changes to this Policy</h2>
          <p>We may update this page as our practices or services change. When we post changes, the date at the top will be updated. If a change affects your rights in a meaningful way, we will provide additional notice.</p>
        </article>

        <article id="contact" className="privacy-section">
          <h2 className="privacy-section-title">Contact</h2>
          <p>If you have questions about this policy or how we handle information, please reach out.</p>
          <div className="privacy-contact-ctas">
            <Link to="/contact" className="p-btn primary">Contact Us</Link>
            <a href="#privacy-hero-title" className="p-btn light">Back to Top</a>
          </div>
        </article>
      </section>

      {/* STICKY FOOTER CTA */}
      <div className="privacy-sticky-cta" role="complementary" aria-label="Contact">
        <div className="privacy-sticky-inner">
          <div className="privacy-sticky-text">
            Have a privacy question or a request about your information
          </div>
          <Link to="/contact" className="p-btn primary">Contact Us</Link>
        </div>
      </div>
    </div>
  );
}
